import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { getNestDependencyInfo, getEffectiveDependencyIds } from '../../shared/nestDependencyInfo';

/**
 * useNestDrop Hook
 * 
 * Detects when a dragged node overlaps another node during drag,
 * enabling "drag to nest" functionality.
 */
export function useNestDrop({
  tasks,
  nodePositions,
  nodeDimensions,
  draggingNode,
  selectedNodes,
}) {
  // Current drop target (node being hovered over)
  const [dropTarget, setDropTarget] = useState(null);
  
  // Pending nest action (when modal is shown)
  const [pendingNestAction, setPendingNestAction] = useState(null);
  
  // Ref to track last update time (debounce)
  const lastUpdateRef = useRef(0);
  
  // Clear drop target if it references a task that no longer exists
  // This prevents "stuck" drop target styling after task deletion or other changes
  useEffect(() => {
    if (dropTarget && !tasks.some(t => t.id === dropTarget.id)) {
      console.log('[useNestDrop] Clearing stale drop target:', dropTarget.id);
      setDropTarget(null);
    }
  }, [tasks, dropTarget]);
  
  // Clear drop target when not dragging
  useEffect(() => {
    if (!draggingNode && dropTarget) {
      console.log('[useNestDrop] Clearing drop target - no active drag');
      setDropTarget(null);
    }
  }, [draggingNode, dropTarget]);

  // Get dimensions for a node
  const getNodeDimensions = useCallback((taskId) => {
    return nodeDimensions[taskId] || { width: 290, height: 150 };
  }, [nodeDimensions]);

  // Check if two rectangles overlap significantly (at least 50% overlap)
  const checkOverlap = useCallback((rect1, rect2) => {
    const overlapX = Math.max(0, Math.min(rect1.x + rect1.width, rect2.x + rect2.width) - Math.max(rect1.x, rect2.x));
    const overlapY = Math.max(0, Math.min(rect1.y + rect1.height, rect2.y + rect2.height) - Math.max(rect1.y, rect2.y));
    
    const overlapArea = overlapX * overlapY;
    const smallerArea = Math.min(rect1.width * rect1.height, rect2.width * rect2.height);
    
    // Require at least 30% overlap of the smaller node
    return overlapArea / smallerArea > 0.3;
  }, []);

  // Get all nodes being dragged (single or multi-select)
  const draggedNodeIds = useMemo(() => {
    if (!draggingNode) return new Set();
    if (selectedNodes.has(draggingNode) && selectedNodes.size > 1) {
      return selectedNodes;
    }
    return new Set([draggingNode]);
  }, [draggingNode, selectedNodes]);

  const getEffectiveDepIds = useCallback((task) => getEffectiveDependencyIds(task, tasks), [tasks]);

  // Check if a task can be a valid drop target
  const isValidDropTarget = useCallback((targetTask, draggedIds) => {
    // Can't drop onto itself
    if (draggedIds.has(targetTask.id)) return false;
    
    // Can't drop onto a task that's being dragged
    if (draggedIds.has(targetTask.id)) return false;
    
    // Can't drop onto a task that depends on any dragged task (would create circular dependency)
    const targetDepIds = getEffectiveDepIds(targetTask);
    for (const depId of targetDepIds) {
      if (draggedIds.has(depId)) return false;
    }
    
    // Check if any dragged task depends on target (also circular)
    for (const draggedId of draggedIds) {
      const draggedTask = tasks.find(t => t.id === draggedId);
      if (!draggedTask) continue;
      
      const draggedDepIds = getEffectiveDepIds(draggedTask);
      for (const depId of draggedDepIds) {
        if (depId === targetTask.id) return false;
      }
    }
    
    return true;
  }, [tasks, getEffectiveDepIds]);

  // Update drop target based on current drag position
  const updateDropTarget = useCallback((draggedNodeId, currentPosition) => {
    if (!draggedNodeId) {
      setDropTarget(null);
      return null;
    }

    // Debounce updates (16ms = ~60fps)
    const now = Date.now();
    if (now - lastUpdateRef.current < 16) return dropTarget;
    lastUpdateRef.current = now;

    const draggedDims = getNodeDimensions(draggedNodeId);
    const draggedRect = {
      x: currentPosition.x,
      y: currentPosition.y,
      width: draggedDims.width,
      height: draggedDims.height,
    };

    // Find the best overlapping target
    let bestTarget = null;
    let bestOverlap = 0;

    for (const task of tasks) {
      if (!isValidDropTarget(task, draggedNodeIds)) continue;

      const targetPos = nodePositions[task.id];
      if (!targetPos) continue;

      const targetDims = getNodeDimensions(task.id);
      const targetRect = {
        x: targetPos.x,
        y: targetPos.y,
        width: targetDims.width,
        height: targetDims.height,
      };

      if (checkOverlap(draggedRect, targetRect)) {
        // Calculate overlap amount for priority
        const overlapX = Math.max(0, Math.min(draggedRect.x + draggedRect.width, targetRect.x + targetRect.width) - Math.max(draggedRect.x, targetRect.x));
        const overlapY = Math.max(0, Math.min(draggedRect.y + draggedRect.height, targetRect.y + targetRect.height) - Math.max(draggedRect.y, targetRect.y));
        const overlapArea = overlapX * overlapY;

        if (overlapArea > bestOverlap) {
          bestOverlap = overlapArea;
          bestTarget = task;
        }
      }
    }

    setDropTarget(bestTarget);
    return bestTarget;
  }, [tasks, nodePositions, getNodeDimensions, checkOverlap, isValidDropTarget, draggedNodeIds, dropTarget]);

  // Clear drop target
  const clearDropTarget = useCallback(() => {
    setDropTarget(null);
  }, []);

  // Calculate dependency info for the dragged nodes
  const getDependencyInfo = useCallback(
    (draggedIds) => getNestDependencyInfo(tasks, draggedIds),
    [tasks],
  );

  // Initiate nest action (show modal)
  // Note: draggedIds is passed directly because draggingNode may be cleared by the time this is called
  const initiateNestAction = useCallback((targetTask, draggedIds) => {
    if (!targetTask || !draggedIds || draggedIds.length === 0) return;

    const draggedSet = new Set(draggedIds);
    const dependencyInfo = getDependencyInfo(draggedSet);
    const hasDependencies = dependencyInfo.upstreamCount > 0 || dependencyInfo.downstreamCount > 0;

    setPendingNestAction({
      targetTask,
      draggedIds,
      dependencyInfo,
      hasDependencies,
    });
  }, [getDependencyInfo]);

  // Cancel pending nest action
  const cancelNestAction = useCallback(() => {
    setPendingNestAction(null);
  }, []);

  // Confirm nest action with selected strategy
  const confirmNestAction = useCallback((strategy) => {
    if (!pendingNestAction) return null;

    const result = {
      targetTaskId: pendingNestAction.targetTask.id,
      draggedIds: pendingNestAction.draggedIds,
      strategy, // 'break' | 'patch' | 'chain'
      dependencyInfo: pendingNestAction.dependencyInfo,
    };

    setPendingNestAction(null);
    return result;
  }, [pendingNestAction]);

  return {
    // State
    dropTarget,
    pendingNestAction,
    draggedNodeIds,

    // Actions
    updateDropTarget,
    clearDropTarget,
    initiateNestAction,
    cancelNestAction,
    confirmNestAction,
    getDependencyInfo,
    isValidDropTarget,
  };
}

export default useNestDrop;

